<template>
    <div :class="classes.wrapper">
        <div
            v-show="showToolbar"
            :class="classes.toolbar">
            <div role="search">
                <label
                    v-show="showSearch"
                    :class="classes.searchLabel"
                    :for="`dataTableSearch${ uid }`">
                    {{ languageData.searchLabel }}
                    <input
                        :id="`dataTableSearch${ uid }`"
                        v-model="searchTerm"
                        :class="classes.searchInput"
                        type="text">
                </label>
            </div>
            <label
                v-show="showPagesize"
                :class="classes.pageSizeLabel"
                :for="`dataTablePageSize${ uid }`">
                {{ languageData.pageSizeLabel }}
                <select
                    :id="`dataTablePageSize${ uid }`"
                    v-model="rowsPerPage"
                    :class="classes.searchInput">
                    <option
                        v-for="option in rowsPerPageOptions"
                        :key="option">
                        {{ option }}
                    </option>
                </select>
            </label>
        </div>

        <table
            :id="uniqueTableId"
            :class="classes.table">
            <thead :class="classes.thead">
                <tr :class="classes.theadTr">
                    <th
                        v-for="(th, thIndex) in columns"
                        :key="`${ th.name }--${ thIndex }`"
                        :aria-controls="uniqueTableId"
                        :aria-label="(!th.name && thIndex === 0) ? trans('GLOBAL_CHARACTERISTIC') : undefined"
                        scope="col"
                        tabindex="0"
                        :class="headerClasses(thIndex)"
                        @click="setSortColumnIndex(thIndex)"
                        @keyup.enter="setSortColumnIndex(thIndex)">
                        <span
                            v-if="th.html"
                            v-html="th.name"></span>
                        <template v-else>
                            {{ th.name }}
                        </template>
                    </th>
                </tr>
            </thead>

            <tbody
                aria-live="polite"
                aria-relevant="all"
                :class="classes.tbody">
                <tr
                    v-for="(row, rowIndex) in pagedRows"
                    :key="rowIndex"
                    :class="classes.tbodyTr"
                    @click="$emit('click', row)"
                    @keyup.enter="$emit('click', row)">
                    <td
                        v-for="(col, colIndex) in row"
                        :key="colIndex"
                        :class="[columnClasses(colIndex), setNoWrapClass(col.value)]"
                        :style="{ backgroundColor: col.backgroundColor }">
                        <span
                            v-if="col.trend"
                            :data-trend="col.trend"></span>
                        <span
                            v-if="columns[colIndex] && columns[colIndex].html"
                            v-html="col.value"></span>
                        <template v-else>
                            <div>
                                {{ col.value }}
                            </div>
                            <Tooltip
                                v-if="col.description"
                                :hide-on-mobile="true"
                                :content="col.description"
                                :settings="{ position: 'left' }">
                                <svg
                                    class="stat__svgIcon--small opacity-50"
                                    :aria-describedby="trans('OUTLOOK_ADDITIONAL_INFORMATION_SHOW')">
                                    <use href="#iconInfoCircle" />
                                </svg>
                            </Tooltip>
                        </template>
                    </td>
                </tr>
            </tbody>
            <tbody v-if="sortedRows.length === 0">
                <tr>
                    <td
                        :class="classes.empty"
                        :colspan="columns.length">
                        <slot name="noResults">
                            No matching records
                        </slot>
                    </td>
                </tr>
            </tbody>
        </table>

        <div
            v-if="showFooter"
            :class="classes.footer">
            <div
                v-if="showRangeLabel"
                :class="classes.rangeLabel">
                {{ pageRangeLabelFormatted }}
            </div>
            <div
                v-if="pagerVisible"
                data-test="dataTable__pager">
                <button
                    :class="classes.pagePrev"
                    :disabled="currentPage === defaultPage"
                    aria-label="Show previous page"
                    type="button"
                    @click="prevPage"
                    @keyup.enter="prevPage">
                    {{ languageData.pagePrev }}
                </button>
                <template v-if="showPageNumber">
                    {{ currentPage }}
                </template>
                <button
                    :class="classes.pageNext"
                    :disabled="currentPage === pagesCount"
                    aria-label="Show next page"
                    type="button"
                    @click="nextPage"
                    @keyup.enter="nextPage">
                    {{ languageData.pageNext }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import defaultSortFn from './sortLocaleCompare';
    import Tooltip from 'components/tooltip';
    import Translator from 'tools/trans';

    const PLACEHOLDER_FROM = '_START_';
    const PLACEHOLDER_TO = '_END_';
    const PLACEHOLDER_TOTAL = '_TOTAL_';
    const PLACEHOLDER_MAX = '_MAX_';

    const SORT_ASCENDING = 'asc';
    const SORT_DESCENDING = 'desc';

    const NO_WRAP_CLASS = 'nowrap';

    const DEFAULT_SORT_PROP = 'value';
    const DEFAULT_PAGE_NUMBER = 1;
    const DEFAULT_PAGE_SIZE_OPTIONS = [50, 100, 500];
    const DEFAULT_LANGUAGE_DATA = {
        pagePrev: '<',
        pageNext: '>',
        searchLabel: 'Search:',
        pageSizeLabel: 'Page size:',
        pageRangeLabel: '_START_ to _END_',
    };
    const DEFAULT_SORT_FUNCTIONS = {
        default: defaultSortFn,
    };

    export default {
        components: { Tooltip },
        props: {
            rows: { type: Array, required: true },
            columns: { type: Array, required: true },
            columnClass: { type: Array, default: () => ([]) },
            filterColumn: { type: Array, default: () => ([]) },
            classes: { type: Object, default: () => ({}) },
            labels: { type: Object, default: () => ({}) },
            pageSize: { type: Number, default: null },
            pageSizeOptions: { type: Array, default: () => DEFAULT_PAGE_SIZE_OPTIONS },
            sortColumn: { type: Number, default: null },
            sortProp: { type: String, default: DEFAULT_SORT_PROP },
            filterProp: { type: String, default: DEFAULT_SORT_PROP },
            mountedCallback: { type: Function, default: () => undefined },
            sortFunctions: { type: Object, default: () => DEFAULT_SORT_FUNCTIONS },
            search: { type: String, default: '' },
            disableSort: { type: Boolean, default: false },
            showPageNumber: { type: Boolean, default: false },
            showToolbar: { type: Boolean, default: true },
            showFooter: { type: Boolean, default: true },
            showRangeLabel: { type: Boolean, default: true },
            showSearch: { type: Boolean, default: true },
            showPagesize: { type: Boolean, default: true },
            showPager: { type: Boolean, default: true },
            getRowsToIgnoreOnSort: { type: Function, default: null },
        },
        data() {
            return {
                uniqueTableId: `data-table-${ this.uid }`,
                sortColumnIndex: this.sortColumn,
                sortDirection: SORT_ASCENDING,
                defaultPage: DEFAULT_PAGE_NUMBER,
                currentPage: DEFAULT_PAGE_NUMBER,
                rowsPerPage: this.pageSize ? this.pageSize.toString() : this.pageSizeOptions[0],
                rowsPerPageOptions: this.pageSizeOptions.map(option => option.toString()),
                internalSearch: '',
                languageData: {
                    pagePrev: this.getLabelValue(this.labels.pagePrev, DEFAULT_LANGUAGE_DATA.pagePrev),
                    pageNext: this.getLabelValue(this.labels.pageNext, DEFAULT_LANGUAGE_DATA.pageNext),
                    searchLabel: this.getLabelValue(this.labels.searchLabel, DEFAULT_LANGUAGE_DATA.searchLabel),
                    pageSizeLabel: this.getLabelValue(this.labels.pageSizeLabel, DEFAULT_LANGUAGE_DATA.pageSizeLabel),
                    pageRangeLabel: this.getLabelValue(this.labels.pageRangeLabel, DEFAULT_LANGUAGE_DATA.pageRangeLabel),
                },
            };
        },
        computed: {
            pageRangeFrom() {
                return (this.currentPage - 1) * this.rowsPerPage + 1;
            },
            pagesCount() {
                return Math.ceil(this.sortedRows.length / parseInt(this.rowsPerPage));
            },
            pageRangeTo() {
                return (this.currentPage * this.rowsPerPage) < this.rows.length
                    ? this.currentPage * this.rowsPerPage
                    : this.rows.length;
            },
            searchTerm: {
                get() {
                    return this.search ? this.search : this.internalSearch;
                },
                set(value) {
                    this.internalSearch = value;
                },
            },
            pageRangeLabelFormatted() {
                if (!this.languageData.pageRangeLabel) {
                    return '';
                }

                return this.languageData.pageRangeLabel
                    .replace(PLACEHOLDER_FROM, this.pageRangeFrom)
                    .replace(PLACEHOLDER_TO, this.pageRangeTo)
                    .replace(PLACEHOLDER_TOTAL, this.sortedRows.length.toString())
                    .replace(PLACEHOLDER_MAX, this.rows.length.toString());
            },
            sortedRows() {
                let preparedData = this.rows.concat().filter(row => this.columnFilter(row));
                preparedData = preparedData.filter(row => this.searchFilter(row));

                const ignoredRows = {};
                if (this.getRowsToIgnoreOnSort && preparedData.length > 1) {
                    const ignoreRowsOnSort = this.getRowsToIgnoreOnSort(preparedData);
                    if (ignoreRowsOnSort.length) {
                        ignoreRowsOnSort.reverse().forEach(rowIndex => {
                            if (preparedData[rowIndex]) {
                                ignoredRows[rowIndex] = preparedData[rowIndex];
                                preparedData.splice(rowIndex, 1);
                            }
                        });
                    }
                }

                if (this.sortColumnIndex !== null) {
                    const sortColumnType = this.columns[this.sortColumnIndex].sortMethod || 'default';
                    const sortFunction = this.getSortFunction(sortColumnType);
                    preparedData.sort((a, b) => {
                        const aValue = a[this.sortColumnIndex] && a[this.sortColumnIndex][this.sortProp] !== '' ? a[this.sortColumnIndex][this.sortProp] : '0';
                        const bValue = b[this.sortColumnIndex] && b[this.sortColumnIndex][this.sortProp] !== '' ? b[this.sortColumnIndex][this.sortProp] : '0';
                        const modifier = this.sortDirection === SORT_DESCENDING ? -1 : 1;

                        return modifier * sortFunction(aValue, bValue);
                    });
                }

                if (Object.keys(ignoredRows).length) {
                    Object.keys(ignoredRows).forEach(rowIndex => {
                        preparedData.splice(parseInt(rowIndex, 10), 0, ignoredRows[rowIndex]);
                    });
                }

                return preparedData;
            },
            pagedRows() {
                return this.sortedRows.filter((row, index) => {
                    const start = (this.currentPage - 1) * parseInt(this.rowsPerPage);
                    const end = this.currentPage * parseInt(this.rowsPerPage);

                    return index >= start && index < end;
                });
            },
            pagerVisible() {
                return this.rows.length > parseInt(this.rowsPerPage) && this.showPager;
            },
        },
        watch: {
            filterColumn() {
                this.resetPage();
            },
            search() {
                this.resetPage();
            },
            pageSize(newValue) {
                this.rowsPerPage = newValue;
            },
            rowsPerPage() {
                this.resetPage();
            },
        },
        methods: {
            trans: Translator.trans,
            columnClasses(columnIndex) {
                const tbodyTrTd = this.classes.tbodyTrTd ? this.classes.tbodyTrTd : null;
                if (columnIndex < this.columnClass.length) {
                    if (tbodyTrTd) {
                        return `${ tbodyTrTd } ${ this.columnClass[columnIndex] }`;
                    } else {
                        return this.columnClass[columnIndex];
                    }
                }

                return tbodyTrTd;
            },
            headerClasses(columnIndex) {
                const defaultHeaderClasses = this.classes.theadTrTh ? this.classes.theadTrTh : '';
                const columnClass = this.columnClass[columnIndex];
                const sortingClass = !(this.disableSort) ? this.classes.sort : 'sorting_disabled';
                if (columnIndex === this.sortColumnIndex) {
                    const sortingDirectionClass = (this.sortDirection === 'asc') ? this.classes.sortAsc : this.classes.sortDesc;

                    return `${ columnClass } ${ defaultHeaderClasses } ${ sortingClass } ${ sortingDirectionClass }`;
                }

                return `${ columnClass } ${ defaultHeaderClasses } ${ sortingClass }`;
            },
            getSortFunction(columnSortType = 'default') {
                return typeof this.sortFunctions[columnSortType] === 'function' ?
                    this.sortFunctions[columnSortType] :
                    this.sortFunctions.default;
            },
            searchFilter(row) {
                for (let i = 0; i < row.length; i++) {
                    const cell = row[i];
                    const cleanCellValue = this.cleanValue(cell[this.filterProp]);
                    const cleanSearchValue = this.cleanValue(this.searchTerm);
                    if (cleanCellValue.indexOf(cleanSearchValue) > -1) {
                        return true;
                    }
                }

                return false;
            },
            columnFilter(row) {
                const filterColumns = this.filterColumn;
                if (filterColumns) {
                    for (let i = 0; i < filterColumns.length; i++) {
                        const filterColumn = filterColumns[i];
                        if (filterColumn.query !== 'undefined' && filterColumn.query !== '') {
                            const cell = row[filterColumn.column];
                            const cleanCellValue = this.cleanValue(cell[this.filterProp]);
                            const cleanSearchValue = this.cleanValue(filterColumn.query);
                            if (cleanCellValue.indexOf(cleanSearchValue) === -1) {
                                return false;
                            }
                        }
                    }
                }

                return true;
            },
            setSortColumnIndex(columnIndex) {
                if (columnIndex === this.sortColumnIndex) {
                    this.sortDirection = this.sortDirection === SORT_ASCENDING ? SORT_DESCENDING : SORT_ASCENDING;
                } else {
                    this.sortDirection = SORT_ASCENDING;
                }
                if (this.disableSort === false) {
                    this.sortColumnIndex = columnIndex;
                }
            },
            nextPage() {
                if ((this.currentPage * parseInt(this.rowsPerPage)) < this.rows.length) {
                    this.currentPage++;
                }
            },
            prevPage() {
                if (this.currentPage > this.defaultPage) {
                    this.currentPage--;
                }
            },
            resetPage() {
                this.currentPage = this.defaultPage;
            },
            cleanValue(stringValue) {
                if (stringValue === null) {
                    return '';
                }

                return String(stringValue).replace(/\s/g, '').toLowerCase();
            },
            getLabelValue(labelValue, defaultLabelValue) {
                return typeof labelValue === 'string' ? labelValue : defaultLabelValue;
            },
            setNoWrapClass(value) {
                if (!isNaN(parseInt(value.replace(/ /g, '')))) {
                    return NO_WRAP_CLASS;
                }
            },
        },
        mounted() {
            this.mountedCallback();
        },
        beforeCreate() {
            this.uid = Date.now();
        },
    };
</script>
